import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalContentProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalTitleProps {
  children: React.ReactNode;
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full mx-4',
};

function Modal({ isOpen, onClose, children, className, size = 'lg' }: ModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Content */}
      <div
        className={cn(
          'relative bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-hidden',
          sizeClasses[size],
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ children, className }: ModalHeaderProps) {
  return <div className={cn('border-b border-gray-200 p-6', className)}>{children}</div>;
}

function ModalContent({ children, className }: ModalContentProps) {
  return <div className={cn('flex-1 overflow-hidden', className)}>{children}</div>;
}

function ModalFooter({ children, className }: ModalFooterProps) {
  return <div className={cn('border-t border-gray-200 p-6', className)}>{children}</div>;
}

function ModalTitle({ children, className }: ModalTitleProps) {
  return <h2 className={cn('text-xl font-semibold text-gray-900', className)}>{children}</h2>;
}

function ModalCloseButton({ onClose, className }: { onClose: () => void; className?: string }) {
  return (
    <button
      onClick={onClose}
      className={cn(
        'absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors',
        className,
      )}
    >
      <X className="w-5 h-5 text-gray-500" />
    </button>
  );
}

export { Modal, ModalHeader, ModalContent, ModalFooter, ModalTitle, ModalCloseButton };
